import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  BookOpen,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  DollarSign,
  CheckCircle,
  XCircle,
  ArrowRight,
  Save,
  X,
  ArrowLeft,
  UserPlus,
  AlertCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useStudents } from '../students/StudentProvider';
import { useClasses } from '../classes/ClassesContext';
import { useEnrollments } from './EnrollmentsProvider';

const AddEnrollment = () => {
  const navigate = useNavigate();
  const { students, loading: studentsLoading, fetchStudents } = useStudents();
  const { classes, loading: classesLoading, fetchClasses } = useClasses();
  const { createEnrollment, loading: enrollmentLoading } = useEnrollments();
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [classSearchTerm, setClassSearchTerm] = useState('');
  const [enrollmentData, setEnrollmentData] = useState({
    enrollmentDate: new Date().toISOString().split('T')[0],
    paymentStatus: 'pending',
    notes: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '', show: false });
  const [activeTab, setActiveTab] = useState('students');

  // Load students and classes on component mount
  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  // Students and classes are now loaded from their respective providers

  // Filter students based on search
  const filteredStudents = students.filter(student => {
    // Handle different property names from backend vs frontend
    const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
    const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
    const parentName = student.parentName || student.parent_name || '';
    const parentEmail = student.parentEmail || student.parent_email || '';
    
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    const searchTerm = studentSearchTerm.toLowerCase();
    return fullName.includes(searchTerm) || 
           parentName.toLowerCase().includes(searchTerm) ||
           parentEmail.toLowerCase().includes(searchTerm);
  });

  // Filter classes based on search and availability
  const filteredClasses = classes.filter(cls => {
    const searchTerm = classSearchTerm.toLowerCase();
    // Handle different property names and ensure we have valid data
    const maxCapacity = cls.maxCapacity || 0;
    const currentEnrollment = cls.currentEnrollment || 0;
    const hasSpots = currentEnrollment < maxCapacity;
    
    const title = cls.title || '';
    const instructor = cls.instructor || '';
    const category = cls.category || '';
    
    const matchesSearch = title.toLowerCase().includes(searchTerm) ||
                         instructor.toLowerCase().includes(searchTerm) ||
                         category.toLowerCase().includes(searchTerm);
    return hasSpots && matchesSearch;
  });

  const handleStudentSelect = (student, isChecked) => {
    if (isChecked) {
      setSelectedStudents(prev => [...prev, student]);
    } else {
      setSelectedStudents(prev => prev.filter(s => s.id !== student.id));
    }
  };

  const handleSelectAllStudents = (isChecked) => {
    if (isChecked) {
      setSelectedStudents(filteredStudents);
    } else {
      setSelectedStudents([]);
    }
  };

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEnrollmentData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const showNotification = (type, message) => {
    setNotification({ type, message, show: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedStudents.length === 0 || !selectedClass) {
      showNotification('error', 'Please select at least one student and a class.');
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmEnrollment = async () => {
    try {
      const successfulEnrollments = [];
      const failedEnrollments = [];
      
      // Process each selected student
      for (const student of selectedStudents) {
        try {
          const enrollmentPayload = {
            student_id: student.id,
            class_id: selectedClass.id,
            enrollment_date: enrollmentData.enrollmentDate,
            payment_status: enrollmentData.paymentStatus,
            enrollment_status: 'active',
            notes: enrollmentData.notes,
            amount_paid: 0 // Default to 0, can be updated later
          };
          
          await createEnrollment(enrollmentPayload);
          successfulEnrollments.push(student);
          
        } catch (error) {
          console.error(`Failed to enroll ${student.studentFirstName || student.student_first_name}:`, error);
          failedEnrollments.push({ student, error: error.message });
        }
      }
      
      // Show appropriate success/error messages
      if (successfulEnrollments.length > 0) {
        const studentNames = successfulEnrollments.map(s => 
          `${s.studentFirstName || s.student_first_name} ${s.studentLastName || s.student_last_name}`
        ).join(', ');
        showNotification('success', `Successfully enrolled ${successfulEnrollments.length} student(s): ${studentNames}`);
        
        // If all enrollments were successful, redirect to enrollments page after a brief delay
        if (failedEnrollments.length === 0) {
          setTimeout(() => {
            navigate('/enrollments');
          }, 2000); // 2 second delay to show the success message
        }
      }
      
      if (failedEnrollments.length > 0) {
        const failureMessages = failedEnrollments.map(f => 
          `${f.student.studentFirstName || f.student.student_first_name}: ${f.error}`
        ).join('; ');
        showNotification('error', `Failed to enroll ${failedEnrollments.length} student(s): ${failureMessages}`);
        
        // If there were partial failures but some successes, still redirect after longer delay
        if (successfulEnrollments.length > 0) {
          setTimeout(() => {
            navigate('/enrollments');
          }, 4000); // 4 second delay to show both success and error messages
        }
      }
      
      // Reset form
      setSelectedStudents([]);
      setSelectedClass(null);
      setEnrollmentData({
        enrollmentDate: new Date().toISOString().split('T')[0],
        paymentStatus: 'pending',
        notes: ''
      });
      setShowConfirmation(false);
      
    } catch (error) {
      console.error('Failed to create enrollment:', error);
      
      // Handle specific error cases
      if (error.message.includes('duplicate key value violates unique constraint')) {
        showNotification('error', 'This student is already enrolled in this class. Please choose a different student or class.');
      } else if (error.message.includes('capacity')) {
        showNotification('error', 'This class is at maximum capacity. Please choose a different class.');
      } else if (error.message.includes('not found')) {
        showNotification('error', 'Selected student or class was not found. Please refresh and try again.');
      } else {
        showNotification('error', `Failed to create enrollment: ${error.message}`);
      }
    }
  };

  const StudentListItem = ({ student, isSelected, onSelect }) => {
    // Handle different property names from backend vs frontend
    const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
    const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
    const age = student.studentAge || student.student_age || student.age || 0;
    const gender = student.gender || '';
    const experience = student.experience || '';
    const enrolledClasses = student.enrolledClasses || student.enrolled_classes || [];
    const photoUrl = student.photoUrl || student.photo_url || null;
    const parentName = student.parentName || student.parent_name || '';
    
    const getGenderPlaceholderStyle = () => {
      switch (gender?.toLowerCase()) {
        case 'female':
          return 'bg-gradient-to-br from-pink-300 to-pink-500';
        case 'male':
          return 'bg-gradient-to-br from-blue-300 to-blue-500';
        default:
          return 'bg-gradient-to-br from-gray-300 to-gray-500';
      }
    };
    
    return (
      <div 
        className={`flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 transition-colors cursor-pointer ${
          isSelected ? 'bg-[#7A99D8] bg-opacity-10' : ''
        }`}
        onClick={() => onSelect(student, !isSelected)}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(student, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded focus:ring-[#7A99D8] focus:ring-2 flex-shrink-0"
        />
        
        {/* Student Photo */}
        <div className="w-10 h-10 ml-3 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
          {photoUrl ? (
            <img 
              src={photoUrl} 
              alt={`${firstName} ${lastName}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`w-full h-full flex items-center justify-center text-white text-xs font-medium ${getGenderPlaceholderStyle()}`}
            style={{ display: photoUrl ? 'none' : 'flex' }}
          >
            {firstName.charAt(0)}{lastName.charAt(0)}
          </div>
        </div>
        
        {/* Student Name */}
        <div className="flex-1 ml-3 min-w-0">
          <div className="font-medium text-[#3C3C3C] dark:text-gray-200 text-sm truncate">
            {firstName} {lastName}
          </div>
        </div>
        
        {/* Age */}
        <div className="w-16 text-sm text-gray-600 dark:text-gray-400 text-center">
          {age}
        </div>
        
        {/* Experience */}
        <div className="w-24 flex justify-center">
          {experience && (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              experience === 'Advanced' ? 'bg-[#FFC6C5] text-[#3C3C3C]' :
              experience === 'Intermediate' ? 'bg-[#FFECC5] text-[#3C3C3C]' :
              experience === 'Beginner' ? 'bg-[#B7F1B6] text-[#3C3C3C]' :
              'bg-[#D9D9D9] text-[#3C3C3C]'
            }`}>
              {experience}
            </span>
          )}
        </div>
        
        {/* Classes */}
        <div className="w-16 text-sm text-gray-600 dark:text-gray-400 text-center">
          {enrolledClasses.length}
        </div>
        
        {/* Parent Name */}
        <div className="w-32 text-sm text-gray-600 dark:text-gray-400 truncate">
          {parentName}
        </div>
      </div>
    );
  };

  const ClassListItem = ({ classItem, isSelected, onSelect }) => {
    // Handle different property names and provide defaults
    const maxCapacity = classItem.maxCapacity || 0;
    const currentEnrollment = classItem.currentEnrollment || 0;
    const spotsLeft = maxCapacity - currentEnrollment;
    const isFull = spotsLeft === 0;

    const title = classItem.title || '';
    const instructor = classItem.instructor || '';
    const skillLevel = classItem.skillLevel || '';
    const startDate = classItem.startDate || '';
    const startTime = classItem.startTime || classItem.start_time || '';
    const duration = classItem.duration || 60;
    const price = classItem.price || 0;
    const dayOfWeek = classItem.dayOfWeek || classItem.day_of_week || '';
    const firstSessionDate = classItem.firstSessionDate || classItem.first_session_date || '';
    const lastSessionDate = classItem.lastSessionDate || classItem.last_session_date || '';

    // Calculate end time from start time and duration
    const calculateEndTime = (start, durationMinutes) => {
      if (!start) return '';
      try {
        const [hours, minutes] = start.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + durationMinutes;
        const endHours = Math.floor(endMinutes / 60) % 24;
        const endMins = endMinutes % 60;
        return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
      } catch (e) {
        return '';
      }
    };

    const endTime = calculateEndTime(startTime, duration);

    // Format time to 12-hour format with AM/PM
    const formatTime = (time) => {
      if (!time) return '';
      try {
        const [hours, minutes] = time.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
      } catch (e) {
        return time;
      }
    };

    // Format session date range
    const formatSessionDates = () => {
      if (!firstSessionDate && !lastSessionDate) return 'TBD';
      if (firstSessionDate && !lastSessionDate) {
        return new Date(firstSessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      if (firstSessionDate && lastSessionDate) {
        const first = new Date(firstSessionDate);
        const last = new Date(lastSessionDate);
        if (first.getMonth() === last.getMonth()) {
          // Same month: "Jan 5 - 26"
          return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${last.getDate()}`;
        } else {
          // Different months: "Jan 28 - Feb 18"
          return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        }
      }
      return 'TBD';
    };

    return (
      <div
        className={`flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 transition-colors cursor-pointer ${
          isFull ? 'opacity-50 cursor-not-allowed bg-red-50 dark:bg-red-900/20' :
          isSelected ? 'bg-[#7A99D8] bg-opacity-10' : ''
        }`}
        onClick={() => !isFull && onSelect(classItem)}
      >
        {/* Radio button */}
        <div className="flex items-center justify-center w-5 h-5 mr-3 flex-shrink-0">
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
            isSelected
              ? 'border-[#7A99D8] bg-[#7A99D8]'
              : 'border-gray-300 dark:border-gray-600'
          }`}>
            {isSelected && (
              <div className="w-2 h-2 rounded-full bg-white"></div>
            )}
          </div>
        </div>

        {/* Class Title & Instructor */}
        <div className="flex-1 min-w-0 mr-4">
          <div className="font-medium text-[#3C3C3C] dark:text-gray-200 text-sm truncate">
            {title}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
            {instructor}
          </div>
        </div>

        {/* Skill Level */}
        <div className="w-24 flex justify-center mr-4 flex-shrink-0">
          {skillLevel && (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              skillLevel === 'Advanced' ? 'bg-[#FFC6C5] text-[#3C3C3C]' :
              skillLevel === 'Intermediate' ? 'bg-[#FFECC5] text-[#3C3C3C]' :
              skillLevel === 'Beginner' ? 'bg-[#B7F1B6] text-[#3C3C3C]' :
              'bg-[#D9D9D9] text-[#3C3C3C]'
            }`}>
              {skillLevel}
            </span>
          )}
        </div>

        {/* Schedule (Day, Time, Dates) */}
        <div className="w-48 text-sm text-gray-600 dark:text-gray-400 mr-4 flex-shrink-0">
          <div className="flex items-center gap-1 mb-0.5">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            <span className="text-xs">{dayOfWeek || 'TBD'}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">• {formatSessionDates()}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span className="text-xs">{startTime && endTime ? `${formatTime(startTime)} - ${formatTime(endTime)}` : 'TBD'}</span>
          </div>
        </div>

        {/* Price */}
        <div className="w-20 text-sm text-gray-600 dark:text-gray-400 text-right mr-4 flex-shrink-0">
          <div className="flex items-center justify-end gap-1">
            <DollarSign className="w-3 h-3" />
            <span className="text-xs">${price}</span>
          </div>
        </div>

        {/* Spots Left */}
        <div className="w-24 text-xs text-right flex-shrink-0">
          <span className={`font-medium ${
            isFull ? 'text-red-600 dark:text-red-400' :
            spotsLeft <= 3 ? 'text-amber-600 dark:text-amber-400' :
            'text-[#7A99D8] dark:text-blue-400'
          }`}>
            {isFull ? 'Class Full' : `${spotsLeft} spots`}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UserPlus className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Create Enrollment</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Enroll a student into a class</p>
            </div>
          </div>
          <Link
            to="/enrollments"
            className="bg-[#F5F5F5] dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Enrollments
          </Link>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setActiveTab('students')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === 'students'
                  ? 'text-[#7A99D8] dark:text-blue-400 bg-[#F5F5F5] dark:bg-gray-700'
                  : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="w-4 h-4" />
                <span>Select Students</span>
                {selectedStudents.length > 0 && (
                  <span className="bg-[#7A99D8] text-white text-xs px-2 py-0.5 rounded-full">
                    {selectedStudents.length}
                  </span>
                )}
              </div>
              {activeTab === 'students' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A99D8]"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => selectedStudents.length > 0 && setActiveTab('class')}
              disabled={selectedStudents.length === 0}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === 'class'
                  ? 'text-[#7A99D8] dark:text-blue-400 bg-[#F5F5F5] dark:bg-gray-700'
                  : selectedStudents.length === 0
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span>Select Class</span>
                {selectedClass && (
                  <CheckCircle className="w-4 h-4 text-[#7A99D8]" />
                )}
              </div>
              {activeTab === 'class' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A99D8]"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => selectedStudents.length > 0 && selectedClass && setActiveTab('details')}
              disabled={!selectedClass || selectedStudents.length === 0}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === 'details'
                  ? 'text-[#7A99D8] dark:text-blue-400 bg-[#F5F5F5] dark:bg-gray-700'
                  : (!selectedClass || selectedStudents.length === 0)
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Enrollment Details</span>
              </div>
              {activeTab === 'details' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A99D8]"></div>
              )}
            </button>
          </div>
        </div>

      <form id="enrollment-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Tab 1: Select Students */}
        {activeTab === 'students' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                selectedStudents.length > 0 ? 'bg-[#7A99D8] bg-opacity-20 text-[#7A99D8]' : 'bg-[#F5F5F5] text-[#3C3C3C]'
              }`}>
                1
              </div>
              <h2 className="text-xl font-semibold text-[#3C3C3C] dark:text-gray-200">Select Students</h2>
              {selectedStudents.length > 0 && (
                <span className="text-sm text-[#7A99D8] dark:text-blue-400 font-medium">
                  {selectedStudents.length} selected
                </span>
              )}
              {selectedStudents.length > 0 && (
                <CheckCircle className="w-5 h-5 text-[#7A99D8]" />
              )}
            </div>
            
            {filteredStudents.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStudents.length === filteredStudents.length}
                  onChange={(e) => handleSelectAllStudents(e.target.checked)}
                  className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded focus:ring-[#7A99D8] focus:ring-2"
                />
                Select All ({filteredStudents.length})
              </label>
            )}
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search students by name, parent name, or email..."
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              />
            </div>
          </div>


          {studentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-gray-200 border-t-[#7A99D8] rounded-full animate-spin mx-auto mb-4"></div>
                <span className="text-gray-600 dark:text-gray-400">Loading students...</span>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              {/* Table Header */}
              <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 p-3">
                <div className="flex items-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  <div className="w-4 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                      onChange={handleSelectAllStudents}
                      className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded focus:ring-[#7A99D8] focus:ring-2"
                    />
                  </div>
                  <div className="flex-1 ml-3">Student</div>
                  <div className="w-16 text-center">Age</div>
                  <div className="w-24 text-center">Level</div>
                  <div className="w-16 text-center">Classes</div>
                  <div className="w-32">Parent</div>
                </div>
              </div>
              
              {/* Student List */}
              <div className="divide-y divide-gray-200 dark:divide-gray-600 max-h-[400px] overflow-y-auto">
                {filteredStudents.map(student => (
                  <StudentListItem
                    key={student.id}
                    student={student}
                    isSelected={selectedStudents.some(s => s.id === student.id)}
                    onSelect={handleStudentSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {!studentsLoading && filteredStudents.length === 0 && (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">No students found</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {studentSearchTerm 
                  ? "Try adjusting your search terms."
                  : students.length === 0
                    ? "No students have been added yet. Add students first to create enrollments."
                    : "No students available for enrollment."
                }
              </p>
            </div>
          )}
        </div>
        )}

        {/* Tab 2: Select Class */}
        {activeTab === 'class' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              selectedClass ? 'bg-[#7A99D8] bg-opacity-20 text-[#7A99D8]' : 'bg-[#F5F5F5] text-[#3C3C3C]'
            }`}>
              2
            </div>
            <h2 className="text-xl font-semibold text-[#3C3C3C] dark:text-gray-200">Select Class</h2>
            {selectedClass && (
              <CheckCircle className="w-5 h-5 text-[#7A99D8]" />
            )}
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search classes by title, instructor, or category..."
                value={classSearchTerm}
                onChange={(e) => setClassSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              />
            </div>
          </div>

          {selectedClass && (
            <div className="mb-4 p-4 bg-[#F5F5F5] dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-[#3C3C3C] dark:text-gray-200">
                    Selected: {selectedClass.title || ''}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedClass.instructor || ''} • {selectedClass.startDate ? new Date(selectedClass.startDate).toLocaleDateString() : 'TBD'} • ${selectedClass.price || 0}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedClass(null)}
                  className="text-[#7A99D8] hover:text-[#3C3C3C] dark:hover:text-gray-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {classesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-gray-200 border-t-[#7A99D8] rounded-full animate-spin mx-auto mb-4"></div>
                <span className="text-gray-600 dark:text-gray-400">Loading classes...</span>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              {/* Table Header */}
              <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 p-3">
                <div className="flex items-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  <div className="w-5 mr-3 flex-shrink-0"></div>
                  <div className="flex-1 mr-4">Class</div>
                  <div className="w-24 text-center mr-4 flex-shrink-0">Level</div>
                  <div className="w-48 mr-4 flex-shrink-0">Schedule</div>
                  <div className="w-20 text-right mr-4 flex-shrink-0">Price</div>
                  <div className="w-24 text-right flex-shrink-0">Availability</div>
                </div>
              </div>

              {/* Class List */}
              <div className="divide-y divide-gray-200 dark:divide-gray-600 max-h-[500px] overflow-y-auto">
                {filteredClasses.map(classItem => (
                  <ClassListItem
                    key={classItem.id}
                    classItem={classItem}
                    isSelected={selectedClass?.id === classItem.id}
                    onSelect={handleClassSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {!classesLoading && filteredClasses.length === 0 && (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">No classes available</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {classSearchTerm 
                  ? "Try adjusting your search terms or check if classes have available spots."
                  : classes.length === 0
                    ? "No classes have been added yet. Add classes first to create enrollments."
                    : "No classes have available spots for enrollment."
                }
              </p>
            </div>
          )}
        </div>
        )}

        {/* Tab 3: Enrollment Details */}
        {activeTab === 'details' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-full bg-[#F5F5F5] dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-200 flex items-center justify-center text-sm font-medium">
              3
            </div>
            <h2 className="text-xl font-semibold text-[#3C3C3C] dark:text-gray-200">Enrollment Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="enrollmentDate" className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">
                Enrollment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="enrollmentDate"
                name="enrollmentDate"
                value={enrollmentData.enrollmentDate}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="paymentStatus" className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">
                Payment Status
              </label>
              <select
                id="paymentStatus"
                name="paymentStatus"
                value={enrollmentData.paymentStatus}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial Payment</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="notes" className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">
              Additional Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={enrollmentData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Any additional information about this enrollment..."
              className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent resize-vertical"
            />
          </div>

          {/* Enrollment Summary */}
          <div className="mt-6 bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-medium text-[#3C3C3C] dark:text-gray-200 mb-4">Enrollment Summary</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Students ({selectedStudents.length})</h4>
                <div className="space-y-2 text-sm max-h-32 overflow-y-auto">
                  {selectedStudents.map((student, index) => {
                    const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
                    const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
                    return (
                      <div key={student.id} className="text-[#3C3C3C] dark:text-gray-200">
                        {index + 1}. {firstName} {lastName}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Class</h4>
                <div className="space-y-1 text-sm text-[#3C3C3C] dark:text-gray-200">
                  <div><strong>Title:</strong> {selectedClass.title || ''}</div>
                  <div><strong>Instructor:</strong> {selectedClass.instructor || ''}</div>
                  <div><strong>Schedule:</strong> {selectedClass.frequency || 'TBD'}, {selectedClass.startTime && selectedClass.endTime ? `${selectedClass.startTime} - ${selectedClass.endTime}` : 'TBD'}</div>
                  <div><strong>Price:</strong> ${selectedClass.price || 0}</div>
                  <div><strong>Start Date:</strong> {selectedClass.startDate ? new Date(selectedClass.startDate).toLocaleDateString() : 'TBD'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Spacer for sticky buttons */}
          <div className="h-20"></div>
        </div>
        )}
      </form>
      </div>

      {/* Sticky Navigation Buttons - Students Tab */}
      {activeTab === 'students' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <Link
              to="/enrollments"
              className="bg-[#F5F5F5] hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Cancel
            </Link>
            <button
              type="button"
              onClick={() => selectedStudents.length > 0 && setActiveTab('class')}
              disabled={selectedStudents.length === 0}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium ${
                selectedStudents.length > 0
                  ? 'bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              Next: Select Class
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Sticky Navigation Buttons - Class Tab */}
      {activeTab === 'class' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('students')}
              className="bg-[#F5F5F5] hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Back: Students
            </button>
            <button
              type="button"
              onClick={() => selectedClass && setActiveTab('details')}
              disabled={!selectedClass}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium ${
                selectedClass
                  ? 'bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              Next: Enrollment Details
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Sticky Navigation Buttons - Details Tab */}
      {activeTab === 'details' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('class')}
              className="bg-[#F5F5F5] hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Back: Select Class
            </button>
            <button
              type="submit"
              form="enrollment-form"
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <Save className="w-5 h-5" />
              Create Enrollments ({selectedStudents.length})
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <CheckCircle className="w-8 h-8 text-[#7A99D8]" />
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200">Confirm Enrollment</h3>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to enroll <strong>{selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''}</strong> in <strong>{selectedClass.title || ''}</strong>?
              </p>
              
              {selectedStudents.length <= 5 && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Students to enroll:</p>
                  <ul className="text-sm text-[#3C3C3C] dark:text-gray-200 space-y-1">
                    {selectedStudents.map((student, index) => {
                      const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
                      const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
                      return (
                        <li key={student.id}>• {firstName} {lastName}</li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleConfirmEnrollment}
                  className="flex-1 bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Confirm {selectedStudents.length} Enrollment{selectedStudents.length !== 1 ? 's' : ''}
                </button>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 bg-[#F5F5F5] hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 max-w-sm w-full rounded-lg shadow-lg border p-4 z-50 ${
          notification.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
              }`}>
                {notification.message}
              </p>
            </div>
            <button
              onClick={() => setNotification(prev => ({ ...prev, show: false }))}
              className={`ml-2 ${
                notification.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEnrollment;